import React, { useMemo, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';
import { formatCurrency } from '../../utils/CurrencyFormat';
import {
  FrequencyPayment,
  PeriodGrain,
  buildPeriodSeries,
} from '../../utils/paymentFrequency';
import { EmptyState, SectionPanel } from '../../shared-components/ui';

interface DebitPaymentTimelineChartProps {
  payments: FrequencyPayment[];
  /** Debit start — anchors empty months/weeks before first payment. */
  rangeStart?: string | null;
  loading?: boolean;
}

const DebitPaymentTimelineChart: React.FC<DebitPaymentTimelineChartProps> = ({
  payments,
  rangeStart,
  loading,
}) => {
  const [grain, setGrain] = useState<PeriodGrain>('month');

  const series = useMemo(
    () =>
      buildPeriodSeries(payments, grain, {
        rangeStart: rangeStart ?? null,
        rangeEnd: new Date(),
      }),
    [payments, grain, rangeStart]
  );

  const hasAnyPayment = series.some((r) => r.count != null && r.count > 0);

  return (
    <SectionPanel
      title="Payments over time"
      subtitle={
        grain === 'month'
          ? 'Monthly totals — blank months had no payments'
          : 'Weekly totals — blank weeks had no payments'
      }
      actions={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={grain}
          onChange={(_, v: PeriodGrain | null) => {
            if (v) setGrain(v);
          }}
        >
          <ToggleButton value="month">Months</ToggleButton>
          <ToggleButton value="week">Weeks</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Loading payment chart…
        </Typography>
      ) : !hasAnyPayment ? (
        <EmptyState
          title="No payments on this debit yet"
          description="Once payments are captured, this chart fills months or weeks — empty periods stay blank."
        />
      ) : (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            {series.filter((r) => r.count == null).length} empty {grain === 'month' ? 'month' : 'week'}
            {series.filter((r) => r.count == null).length === 1 ? '' : 's'} ·{' '}
            {series.filter((r) => r.count != null).length} with payments
          </Typography>
          <Box sx={{ height: 260, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  minTickGap={grain === 'week' ? 12 : 8}
                  angle={grain === 'week' ? -30 : 0}
                  textAnchor={grain === 'week' ? 'end' : 'middle'}
                  height={grain === 'week' ? 50 : 30}
                />
                <YAxis
                  yAxisId="count"
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  width={32}
                />
                <YAxis
                  yAxisId="amount"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  width={52}
                  tickFormatter={(v) =>
                    v == null ? '' : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(1)}L` : `₹${v}`
                  }
                />
                <ReTooltip
                  formatter={(value, name) => {
                    if (value == null || value === '') return ['—', String(name)];
                    const n = Number(value);
                    if (!Number.isFinite(n)) return ['—', String(name)];
                    return name === 'Amount'
                      ? [formatCurrency(n), String(name)]
                      : [n, String(name)];
                  }}
                  labelFormatter={(label) => String(label)}
                />
                <Legend />
                <Bar
                  yAxisId="count"
                  dataKey="count"
                  name="Payments"
                  fill="#0F766E"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={grain === 'week' ? 18 : 28}
                  // null → no bar (blank period)
                />
                <Line
                  yAxisId="amount"
                  type="monotone"
                  dataKey="amount"
                  name="Amount"
                  stroke="#D97706"
                  strokeWidth={2}
                  dot={{ r: 2.5 }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </Stack>
      )}
    </SectionPanel>
  );
};

export default DebitPaymentTimelineChart;
